// The calendar's undo bar, and the create page's hand-off into it (W40.1 F7).
//
// Split out of `routes/Calendar.tsx` because that file is on the §3 #10 ratchet
// and the feature grew it. It is also the right home: the bar has two producers
// now, and one consumer, and only the calendar has either.

import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useUndoBar } from "@/hooks/useUndoBar";
import { deleteEvent } from "@/lib/api/calendar";
import { queryKeys } from "@/lib/queries/keys";
import { successMessage } from "@/components/calendar/eventFanOut";

/** What `/calendar/events/new` passes back through the router's location state. */
export interface CalendarUndoHandoff {
  /** Every row the create produced — one for a range, N for a scattered set. */
  ids: string[];
  /**
   * Rows written, **for the copy**. Equals `ids.length` today; named
   * separately so a future shape that differs does not silently change the
   * wording.
   */
  dayCount: number;
  /** True when the rows came from an **edit** rather than a create. */
  isEdit: boolean;
}

/**
 * What the bar is holding.
 *
 * **There is no "deleted" shape here, and that is the point**（ADR-0215）.
 *
 * The bar on the **month view** has exactly one producer: the create page
 * handing its new rows back. The **delete** lives on `/calendar/events/:id`,
 * and that page owns its **own** bar with its own `restoreEvent` — so the
 * delete's wording and its restore action are both somewhere they belong.
 *
 * The old shape was `{ id: string } | { ids: string[] }`, and the two branches
 * were read as "one row or many" — which is **not** what they meant. They
 * meant "deleted or created", and the copy collapsed them anyway:
 *
 * ```ts
 * "ids" in item && item.ids.length > 1 ? 已建立 N 天 : "事件已刪除"
 * ```
 *
 * A **single-day create** has one id, so it fell through to 「事件已刪除」 —
 * the month view told the user their brand-new event had been deleted, and the
 * bar's 「復原」 button (which *deletes* the rows it created) looked like the
 * way to get it back. **A false statement with a live affordance attached.**
 *
 * Now the shape says what it is, and the type is the contract: a delete on
 * this page would not compile.
 */
interface CalendarUndoItem {
  ids: string[];
  dayCount: number;
  isEdit: boolean;
}

function readHandoff(state: unknown): CalendarUndoHandoff | null {
  if (!state || typeof state !== "object") return null;
  const carried = (state as { calendarUndo?: Partial<CalendarUndoHandoff> }).calendarUndo;
  if (!carried || !Array.isArray(carried.ids) || carried.ids.length === 0) return null;
  return {
    ids: carried.ids,
    dayCount: typeof carried.dayCount === "number" ? carried.dayCount : carried.ids.length,
    isEdit: carried.isEdit === true,
  };
}

export function useCalendarUndoBar(onError: (message: string) => void) {
  const qc = useQueryClient();
  const location = useLocation();
  const nav = useNavigate();

  const undo = useUndoBar<CalendarUndoItem>();

  const restoreMut = useMutation({
    mutationFn: async () => {
      const ids = undo.item!.ids;
      // `allSettled`, not `all`: undo is best-effort by nature, and one row
      // that refuses must not strand the others behind a spinner. The bar
      // clears either way and the month refetches, so whatever really exists
      // is what gets rendered — the UI never claims a reversal that did not
      // happen.
      const settled = await Promise.allSettled(ids.map((id) => deleteEvent(id)));
      const failed = settled.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        // **不是「可能已超過 7 天」。** 那是「復原一筆軟刪除」的語意 ——
        // 這裡復原的是「剛剛建立的一列」，做法是把它刪掉；它剛存在，
        // 不可能過期。（ADR-0215）
        throw new Error(`復原失敗：${failed} 筆無法移除`);
      }
      return ids.length;
    },
    onSuccess: async () => {
      undo.clear();
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      await qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (err) => {
      undo.clear();
      onError(err instanceof Error ? err.message : "復原失敗，請稍候再試");
    },
  });

  /**
   * A create that produced rows comes back here with their ids in the router
   * state, so the bar opens on the month the user is already looking at.
   *
   * Read and then wiped with `replace`, for two reasons: a reload must not
   * re-arm the bar for events the user has long since dealt with, and the create
   * page must not sit between the month and itself in history — the back button
   * would walk into a form that was already submitted.
   */
  useEffect(() => {
    const carried = readHandoff(location.state);
    if (!carried) return;
    undo.begin({
      ids: carried.ids,
      dayCount: carried.dayCount,
      isEdit: carried.isEdit,
    });
    void qc.invalidateQueries({ queryKey: ["calendar-month"] });
    void qc.invalidateQueries({ queryKey: ["calendar-day"] });
    void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
    // `undo.begin` is stable for the hook's lifetime (useCallback on
    // `[durationMs, stop]`), so this really does run once per navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /**
   * The bar's message — **what actually happened**.
   *
   * `successMessage` is the **same function** the form's success toast uses,
   * so the two surfaces cannot disagree（ADR-0215）. One create → 「事件已建立」;
   * a scattered multi-day create → 「已建立 N 天各自的安排」.
   */
  const undoMessage = useCallback(
    (item: CalendarUndoItem): string => successMessage(item.dayCount, item.isEdit),
    [],
  );

  return { undo, restoreMut, undoMessage };
}
